import { contentContext } from './content-catalog.mjs';
const pages = new Set(['/', '/create', '/library', '/me', '/rewards', '/creator/insights', '/pricing', '/billing', '/subscription', '/about', '/feed', '/discover', '/music', '/studio', '/extension', '/guides', '/lyrics-generator', '/rap-lyrics-generator', '/album-cover-generator', '/mv-generator', '/clips', '/vocal-separation']);
export function analyticsPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return '/other';
  if (contentContext(value).content_id) return value.split(/[?#]/)[0].replace(/\/$/, '');
  const path = value.split(/[?#]/)[0].replace(/^\/(?:en|zh|zh-TW|ja|ko|fr|de|es)(?=\/|$)/, '').replace(/\/$/, '') || '/';
  for (const prefix of ['share', 'invite', 'song', 'checkout', 'payment', 'guides']) if (path.startsWith(`/${prefix}/`)) return `/${prefix}/:id`;
  return pages.has(path) ? path : '/other';
}
const enumFields = {
  content_source: ['discover', 'guide', 'tool'],
  content_locale: ['en', 'zh', 'zh-TW', 'ja', 'ko', 'fr', 'de', 'es'],
  entry_source: ['search', 'ai', 'referral', 'direct', 'internal'],
  source: ['share','direct','web','musicmint_extension','pricing','library','create','feed','discover','geo_guides','rap_name_generator','rap_lyrics_generator'],
  destination: ['app_store','app','web_create','web','create','pricing','explore'],
  cta_location: ['top','hero','footer','about','end','post_create','banner','inapp','sticky','header','inline','primary','secondary','hero_secondary','page','player','lyrics','similar','gift','inapp_notice'],
  mode: ['normal','simple','advanced','custom','basic','instrumental','lyrics'],
  action: ['app_store','compose','create','share','copy','download','upgrade'],
  method: ['clerk'],
  ai_referrer: ['chatgpt','perplexity','claude','gemini','copilot','poe','you','phind','deepseek','grok','ai-bot'],
};
Object.assign(enumFields, {
  entry: ['share','song','home','create','pricing','onboarding','download','agent','other','unknown'],
  intent_surface: ['home','create','song_detail','share_page'],
  reason: ['description_empty','description_long','lyrics_empty','lyrics_long','style_long','mood_long','title_long','credits','pending_limit','identity','expired','auth','legacy','storage','uncertain','ready','user'],
  surface: ['share_page','song_page','song_detail','player','library','paywall','pricing','billing','create','music_task','feed','agent','mv','vocal_separation','rap_lyrics','clips','lyrics','rap_name','album_cover','create_lyrics','other','unknown'],
  target: ['instagram','tiktok','facebook','x','messages','whatsapp','copy','save','more'],
  share_kind: ['share','gift'], asset_type: ['link','card','gift_text'],
  delivery_method: ['clipboard','web_share','manual'],
  result: ['copy_success','copy_failed','system_sheet_returned','dismissed','error','ready','account_redirect','submitting','accepted','blocked','discarded'],
  error_stage: ['platform','audio','checkout'],
  error_code: ['unknown','not_allowed','unavailable','media_aborted','media_network','media_decode','media_src_not_supported','network','unauthorized','conflict','unavailable_product','invalid_url'],
  provider: ['apple','stripe','fastspring','unknown'],
  plan_type: ['week','month','year','lifetime','credits','other'],
  paywall_type: ['subscription','credits'],
  trigger: ['insufficient_credits','download','manual','onboarding'],
  stage: ['checkout_ready','account_redirect','auth','account','quote','frontend','server'], amount_source: ['catalog','store_quote','verified_transaction'],
  kind: ['subscription','upgrade'],
});
const sharedFields = new Set(['page_path','content_id','content_source','content_locale','entry_source','source','entry','surface','schema_version','has_gift','has_lyrics','is_ios','is_logged_in','is_vip','destination','cta_location','mode','action','method','ai_referrer','campaign_token']);
const paymentFields = ['product_id','plan_type','provider','paywall_type','trigger'];
// CTA targets describe an action; share_click/share_result targets remain social platforms.
const shareCtaEvents = new Set(['share_cta_view','share_cta_click','appstore_click','share_continue_web_click','share_compose_submit','share_create_similar_click','share_send_friend_click']);
const shareCtaEnums = {
  target: ['app_store','web_create','login','share_action'],
  placement: ['after_player','desktop_sidebar','banner','end_card'],
  // Preserve historical destination values; new action values are CTA-only.
  destination: [...enumFields.destination,'login','share_action'],
};
const eventFields = {
  ...Object.fromEntries([...shareCtaEvents].map(event => [event, ['target','placement']])),
  create_view: ['intent_surface'], create_submit_click: ['intent_surface'],
  home_create_cta_click: ['intent_surface'],
  create_validation_failed: ['intent_surface','reason'],
  create_ready: ['intent_surface','stage'],
  generate_resume_result: ['intent_surface','result','reason'],
  generate_blocked_login: ['intent_surface'],
  generate_blocked_credits: ['intent_surface','stage','balance','required_points'],
  generate_submit: ['intent_surface'],
  song_detail_create_similar_click: ['intent_surface'],
  share_click: ['target','share_kind','asset_type'],
  share_result: ['target','share_kind','asset_type','delivery_method','result','error_stage','error_code'],
  gift_link_create: ['share_kind'], gift_link_share: ['share_kind'],
  share_play_attempt: [], share_play_error: ['error_code','error_stage','elapsed_ms'], share_audio_play: ['elapsed_ms'],
  paywall_view: ['paywall_type','trigger'], paywall_dismiss: ['paywall_type','trigger'],
  paywall_compare_plans: ['paywall_type','trigger'], paywall_plans_loaded: ['paywall_type','trigger'],
  paywall_cta_click: [...paymentFields,'price','currency','amount_source'],
  checkout_started: paymentFields, checkout_start: paymentFields, checkout_redirect: paymentFields,
  checkout_error: [...paymentFields,'error_stage','error_code'], paywall_checkout_error: paymentFields,
  subscription_upgrade_start: paymentFields,
  purchase_stage: [...paymentFields,'stage','result','error_stage','error_code'],
  purchase_success: [...paymentFields,'kind'],
  purchase_verified: [...paymentFields,'value','currency','amount_source'],
};
// Only IDs read from an actual catalog can pass the outbound SKU field.
const productIds = new Set();
export function registerAnalyticsProducts(catalog) {
  for (const plan of Array.isArray(catalog) ? catalog : []) {
    if (typeof plan?.product_id === 'string' && /^[a-zA-Z0-9._-]{1,80}$/.test(plan.product_id) && productIds.size < 500) productIds.add(plan.product_id);
  }
}
const currencies = new Set(['USD','EUR','GBP','CNY','JPY','KRW','CAD','AUD','TWD','HKD']);
const booleans = new Set(['has_gift','has_lyrics','is_ios','is_logged_in','is_vip']);
export const campaignNames = new Set(['web_footer','web_about','invite_landing', ...['top','hero','end','banner','inapp','sticky','footer'].flatMap(place => [`share_${place}`, `share_gift_${place}`])]);
export function sanitizeAnalyticsParams(input = {}, event = '') {
  const output = {};
  const cta = shareCtaEvents.has(event);
  const enums = cta ? { ...enumFields, ...shareCtaEnums } : enumFields;
  const allowed = new Set([...sharedFields, ...(Object.hasOwn(eventFields, event) ? eventFields[event] : [])]);
  for (const [key, value] of Object.entries(input || {})) {
    if (!allowed.has(key)) continue;
    if (key === 'schema_version' && value === 2) output[key] = value;
    else if (key === 'product_id' && productIds.has(value)) output[key] = value;
    else if (['value','price','elapsed_ms','balance','required_points'].includes(key) && typeof value === 'number' && Number.isFinite(value) && value >= 0) output[key] = value;
    else if (key === 'currency' && currencies.has(value)) output[key] = value;
    else if (key === 'page_path') output[key] = analyticsPath(value);
    else if (key === 'content_id' && typeof value === 'string' && contentContext(`/${value}`).content_id === value) output[key] = value;
    else if (booleans.has(key) && (typeof value === 'boolean' || value === 0 || value === 1)) output[key] = Number(value);
    else if (Object.hasOwn(enums, key) && enums[key].includes(value)) output[key] = value;
    else if (key === 'campaign_token' && campaignNames.has(value)) output[key] = value;
  }
  // destination is the dashboard dimension; retain SH-2's target as a compatible alias.
  // Some existing CTA callers supply only target. Never replace an explicit destination.
  if (cta && input?.destination === undefined && output.target) output.destination = output.target;
  return output;
}
/** No pre-consent history. Only post-consent events may wait for the script. */
export function createAnalyticsDispatcher({ send, enabled = true, now = Date.now, limit = 50, ttl = 300000 }) {
  let consent = 'unknown', ready = false, queue = [], lastPage = '', sequence = 0;
  const delivered = new Set();
  function flush() {
    if (!enabled || consent !== 'granted' || !ready) return;
    const pending = queue; queue = [];
    for (const item of pending) {
      if (now() - item.at > ttl || delivered.has(item.key)) continue;
      try { send(item.name, item.params, item.context); delivered.add(item.key); } catch { /* Analytics never blocks product actions. */ }
      if (delivered.size > 200) delivered.delete(delivered.values().next().value);
    }
  }
  function track(name, params = {}, key, context) {
    if (!enabled || consent !== 'granted' || typeof name !== 'string' || !/^[a-z][a-z0-9_]{0,39}$/.test(name)) return false;
    const itemKey = key || `event:${++sequence}`;
    if (delivered.has(itemKey) || queue.some(item => item.key === itemKey)) return false;
    queue.push({ name, params: sanitizeAnalyticsParams(params, name), context, key: itemKey, at: now() });
    if (queue.length > limit) queue.shift();
    flush(); return true;
  }
  return {
    track,
    setReady(value) { ready = value === true; flush(); },
    setConsent(value) {
      const next = ['granted','denied'].includes(value) ? value : 'unknown';
      if (next !== consent) { queue = []; lastPage = ''; }
      consent = next; flush();
    },
    page(path, context) {
      const clean = analyticsPath(path);
      const identity = typeof path === 'string' ? path.split(/[?#]/)[0] : clean;
      if (consent !== 'granted' || identity === lastPage) return;
      if (track('page_view', {page_path: clean}, undefined, context)) lastPage = identity;
    },
    snapshot: () => ({consent, ready, queued: queue.length}),
  };
}
export function appStoreUrl(campaign, provider = '') {
  const ct = campaignNames.has(campaign) ? campaign : 'web_footer';
  const query = new URLSearchParams({ct, mt:'8'});
  if (/^\d{1,20}$/.test(provider)) query.set('pt', provider);
  return `https://apps.apple.com/app/id6737772961?${query}`;
}
export function appStoreAffiliate(campaign, provider = '') {
  return new URL(appStoreUrl(campaign, provider)).search.slice(1);
}
/** Receipt IDs stay in this tab; only fixed low-cardinality fields leave it. */
export function createSuccessTracker({storage, emit, now = Date.now}) {
  const key = 'musicmint:analytics-success:v1';
  const read = () => { try { const rows = JSON.parse(storage().getItem(key) || '[]'); return Array.isArray(rows) ? rows.filter(row => typeof row?.key === 'string' && Number.isFinite(row.at) && now() - row.at < 86400000).slice(-100) : []; } catch { return []; } };
  function write(rows) { try { storage().setItem(key, JSON.stringify(rows.slice(-100))); return true; } catch { return false; } }
  return {
    remember(owner, task, source = 'direct', context = {}) {
      if (!owner || typeof task !== 'string' || !task || task.length > 256) return;
      const rows = read(), id = `generation:${owner}:${task}`;
      if (!rows.some(row => row.key === id)) write([...rows, {key:id, owner, task, source:enumFields.source.includes(source) ? source : 'direct', context:sanitizeAnalyticsParams(context), at:now(), done:false}]);
    },
    complete(owner, task) {
      if (!owner || task?.status !== 3 || !task?.music_data?.some(song => song.id && (song.audio_url || song.audio_oss))) return;
      const rows = read(), row = rows.find(row => row.key === `generation:${owner}:${task.id}` && !row.done);
      if (!row) return;
      row.done = true;
      if (write(rows)) {
        emit('generate_success', {...row.context, source:row.source, page_path:'/create'});
        // A first success is only claimed for a confirmed new signup in this tab.
        const signup = rows.find(item => item.key === `signup:${owner}` && !item.converted);
        if (signup) {
          signup.converted = true;
          if (write(rows)) emit('first_generate_success', {...row.context, source:row.source, page_path:'/create'});
        }
      }
    },
    signup(userId, sessionId, signup, context = {}) {
      if (!userId || !sessionId || signup?.status !== 'complete' || signup.createdUserId !== userId || signup.createdSessionId !== sessionId) return;
      const rows = read(), id = `signup:${userId}`;
      if (rows.some(row => row.key === id)) return;
      if (write([...rows, {key:id, at:now(), done:true}])) emit('sign_up', {...sanitizeAnalyticsParams(context), method:'clerk'});
    },
  };
}
